import { Arrow, Circle, Group, Label, Line, Rect, Tag, Text } from "react-konva";
import type Konva from "konva";
import type { KonvaEventObject } from "konva/lib/Node";
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import type { Furniture, Vec2 } from "../../lib/types";
import type { FitInfo } from "../../lib/useFitToRoom";
import { resolveFurniture } from "../../lib/furniture/resolve";
import { boundingBox, formatLength, localToWorld, polygonsOverlap } from "../../lib/geom";
import { track } from "../../lib/telemetry";
import { noteFurnitureTransform } from "../../lib/transformTelemetry";
import { TOUCH_DRAG_DISTANCE } from "../../lib/touchPickup";
import { useDesign } from "../../lib/store";
import { FURNITURE_SELECTED_STROKE } from "./FurnitureNode";
import {
  ADJUST_RING,
  HANDLE_METRICS,
  adjustRingLayout,
  resizeFromEdge,
  resizeFromEdgeDelta,
  rotationFromPointer,
  touchEdgeGrip,
  touchHandleLayout,
  touchRotateHit,
  type PxRect,
  type ResizeEdge,
  type TouchHandle,
  type TouchHandleLayout,
} from "../../lib/furniture/transform";

/** `direct`: grips on the piece itself. `adjust`: the touch Adjust mode's knob ring. */
export type HandleMode = "direct" | "adjust";
/** The inspector field a tapped knob stands for. */
export type KnobField = "width" | "depth" | "rotation";

export interface FurnitureHandlesProps {
  furniture: Furniture;
  fit: FitInfo;
  /** Current stage zoom so handles keep a constant on-screen size. */
  stageScale: number;
  unit: "imperial" | "metric";
  /** Grid step for size snapping, inches (0 disables). */
  snapIn: number;
  onChange: (patch: Partial<Furniture>) => void;
  /** Called once at the start of a gesture so the host can push undo history. */
  onGestureStart?: () => void;
  onGestureEnd?: () => void;
  /** Called instead of `onGestureEnd` when a pinch interrupts the gesture. */
  onGestureCancel?: () => void;
  /** Finger-sized grips (coarse pointers). */
  touch?: boolean;
  mode?: HandleMode;
  /**
   * Touch: show direct grips only on pieces large on screen, and not over
   * other pieces (`touchHandleLayout`). Off shows every grip.
   */
  sizeAware?: boolean;
  /** Other pieces' outlines in room inches; touch grips that would land on one are left out. */
  neighbors?: Vec2[][];
  /** A knob was tapped without dragging (Adjust mode). */
  onKnobTap?: (field: KnobField) => void;
}

type Edge = ResizeEdge;
type Pose = Pick<Furniture, "x" | "y" | "width" | "depth" | "rotation">;

const EDGES: Edge[] = ["left", "right", "front", "back"];
const OUTWARD: Record<Edge, Vec2> = {
  left: { x: -1, y: 0 },
  right: { x: 1, y: 0 },
  front: { x: 0, y: 1 },
  back: { x: 0, y: -1 },
};
const SNAP_FLASH_MS = 180;
const ALL_GRIPS: TouchHandleLayout = { show: true, edges: EDGES, rotate: true };

/**
 * Direct-manipulation handles for the selected piece: a rotation grip above
 * the back edge and four edge grips for width / depth. All handles live in
 * the piece's rotated frame so they follow any footprint (L / U sectionals
 * included). Sizes are compensated for stage zoom so grips stay finger-sized.
 *
 * Rotation snaps to 15° with Shift and softly to the cardinal angles;
 * resizing keeps the opposite edge pinned (the natural "pull this side out"
 * mental model) and snaps to the grid step when snapping is on.
 *
 * On touch the piece's body always belongs to moving it: direct grips sit
 * outside the outline and only show on pieces that are large on screen.
 * Adjust mode puts knobs on a ring around the piece instead, big enough to
 * hold even a tiny armchair.
 */
export function FurnitureHandles({
  furniture: f,
  fit,
  stageScale,
  unit,
  snapIn,
  onChange,
  onGestureStart,
  onGestureEnd,
  onGestureCancel,
  touch = false,
  mode = "direct",
  sizeAware = true,
  neighbors,
  onKnobTap,
}: FurnitureHandlesProps) {
  const inv = 1 / Math.max(stageScale, 1e-6);
  const adjust = mode === "adjust";
  const size = HANDLE_METRICS[touch ? "touch" : "mouse"];
  const pxPerIn = fit.scale;
  const center = fit.pointToPx({ x: f.x, y: f.y });
  const resolved = resolveFurniture(f);
  const bb = boundingBox(resolved.footprint);
  const halfW = (bb.width / 2) * pxPerIn;
  const halfD = (bb.height / 2) * pxPerIn;
  /** On-screen half extents, px. */
  const screenHalfW = halfW * stageScale;
  const screenHalfD = halfD * stageScale;

  const [badge, setBadge] = useState<{ text: string; x: number; y: number } | null>(null);
  const [flash, setFlash] = useState<TouchHandle | null>(null);
  const flashTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const gesture = useRef<{
    kind: "rotate" | Edge;
    start: Pose;
    /** Pointer in layer px when the drag began (grab offset). */
    startPointer: Vec2 | null;
    /** Last size / angle shown, to tell when a snap step lands. */
    last: number;
  } | null>(null);
  /** Touch grips stay as they were for the length of a drag. */
  const [frozen, setFrozen] = useState<TouchHandleLayout | null>(null);
  const [gripsShown, setGripsShown] = useState(false);

  useEffect(() => () => {
    if (flashTimer.current) clearTimeout(flashTimer.current);
  }, []);

  const stroke = FURNITURE_SELECTED_STROKE;
  const gripR = size.gripR * inv;
  const rotateY = -halfD - size.rotateOffset * inv;

  const neighborHits = useMemo(() => {
    const hits: Partial<Record<TouchHandle, boolean>> = {};
    if (!touch || adjust || !sizeAware || !neighbors?.length) return hits;
    const screenPerIn = pxPerIn * stageScale;
    if (screenPerIn <= 0) return hits;
    const toWorld = (r: PxRect) =>
      localToWorld(
        [
          { x: r.x, y: r.y },
          { x: r.x + r.width, y: r.y },
          { x: r.x + r.width, y: r.y + r.height },
          { x: r.x, y: r.y + r.height },
        ].map((p) => ({ x: p.x / screenPerIn, y: p.y / screenPerIn })),
        f.x,
        f.y,
        f.rotation
      );
    const lands = (r: PxRect) => {
      const pad = toWorld(r);
      return neighbors.some((n) => polygonsOverlap(pad, n));
    };
    for (const edge of EDGES) hits[edge] = lands(touchEdgeGrip(edge, screenHalfW, screenHalfD).hit);
    const k = touchRotateHit(screenHalfD);
    hits.rotate = lands({ x: k.x - k.r, y: k.y - k.r, width: k.r * 2, height: k.r * 2 });
    return hits;
  }, [touch, adjust, sizeAware, neighbors, pxPerIn, stageScale, f.x, f.y, f.rotation, screenHalfW, screenHalfD]);

  const computed =
    touch && !adjust
      ? sizeAware
        ? touchHandleLayout(screenHalfW * 2, screenHalfD * 2, gripsShown, neighborHits)
        : ALL_GRIPS
      : null;
  if (computed && !frozen && computed.show !== gripsShown) setGripsShown(computed.show);
  const layout = frozen ?? computed;
  const ring = adjust ? adjustRingLayout(screenHalfW * 2, screenHalfD * 2) : null;

  /** Pointer in layer (canvas px) coordinates, independent of stage pan/zoom. */
  function layerPointer(e: KonvaEventObject<DragEvent | Event>): Vec2 | null {
    const stage = e.target.getStage();
    const layer = e.target.getLayer();
    if (!stage || !layer) return null;
    const pointer = stage.getPointerPosition();
    if (!pointer) return null;
    return layer.getAbsoluteTransform().copy().invert().point(pointer);
  }

  /** Pointer position in the piece's local (rotated, px) frame. */
  function localPointer(e: KonvaEventObject<DragEvent>): Vec2 | null {
    const stage = e.target.getStage();
    const group = e.target.getParent() as Konva.Group | null;
    if (!stage || !group) return null;
    const pointer = stage.getPointerPosition();
    if (!pointer) return null;
    const t = group.getAbsoluteTransform().copy().invert();
    return t.point(pointer);
  }

  function pulse(handle: TouchHandle) {
    setFlash(handle);
    if (flashTimer.current) clearTimeout(flashTimer.current);
    flashTimer.current = setTimeout(() => setFlash(null), SNAP_FLASH_MS);
    // Android only; iOS Safari has no vibrate.
    if (touch) navigator.vibrate?.(8);
  }

  function begin(kind: "rotate" | Edge, e: KonvaEventObject<DragEvent>) {
    gesture.current = {
      kind,
      start: { x: f.x, y: f.y, width: f.width, depth: f.depth, rotation: f.rotation },
      startPointer: layerPointer(e),
      last: kind === "rotate" ? f.rotation : kind === "left" || kind === "right" ? f.width : f.depth,
    };
    if (touch && layout) setFrozen(layout);
    onGestureStart?.();
  }

  /**
   * Konva fires `dragend` without a native event when a drag is stopped from
   * code — only the pinch handler does that — so treat it as a cancel.
   */
  function end(e: KonvaEventObject<DragEvent>) {
    const cancelled = !e.evt;
    const g = gesture.current;
    const kind = g ? (g.kind === "rotate" ? "rotate" : "resize") : null;
    if (kind && !cancelled) {
      track("furniture_transformed", { kind, input: touch ? "touch" : "mouse", mode });
    }
    gesture.current = null;
    setBadge(null);
    setFrozen(null);
    if (cancelled && onGestureCancel) {
      onGestureCancel();
      return;
    }
    const depth = useDesign.getState().undoStack.length;
    onGestureEnd?.();
    const after = useDesign.getState().undoStack.length;
    if (kind && after > depth) noteFurnitureTransform(kind, after);
  }

  function handleRotateMove(e: KonvaEventObject<DragEvent>, slot: Vec2) {
    const p = layerPointer(e);
    if (!p) return;
    const deg = rotationFromPointer(center, p, (e.evt as MouseEvent).shiftKey);
    onChange({ rotation: deg });
    // Keep the grip pinned to its slot.
    e.target.position(slot);
    const g = gesture.current;
    if (g && deg !== g.last && deg % 90 === 0) pulse("rotate");
    if (g) g.last = deg;
    setBadge({ text: `${Math.round(deg)}°`, x: slot.x, y: slot.y - 22 * inv });
  }

  function handleEdgeMove(edge: Edge, e: KonvaEventObject<DragEvent>) {
    const lp = localPointer(e);
    if (!lp) return;
    const { patch, halfIn } = resizeFromEdge(
      f,
      edge,
      { x: lp.x / pxPerIn, y: lp.y / pxPerIn },
      snapIn
    );
    const halfPx = halfIn * pxPerIn;
    if (edge === "left" || edge === "right") {
      const sign = edge === "right" ? 1 : -1;
      e.target.position({ x: sign * halfPx, y: 0 });
      setBadge({ text: formatLength(patch.width ?? f.width, unit), x: sign * (halfPx + 26 * inv), y: 0 });
    } else {
      const sign = edge === "front" ? 1 : -1;
      e.target.position({ x: 0, y: sign * halfPx });
      setBadge({ text: formatLength(patch.depth ?? f.depth, unit), x: 0, y: sign * (halfPx + 22 * inv) });
    }
    onChange(patch);
  }

  /**
   * Touch resize: the edge moves by the finger's travel along its normal
   * (grab offset kept, so nothing jumps), and the grip or knob follows.
   */
  function handleTouchEdgeMove(edge: Edge, e: KonvaEventObject<DragEvent>) {
    const g = gesture.current;
    const p = layerPointer(e);
    if (!g || !g.startPointer || !p) return;
    const rad = (g.start.rotation * Math.PI) / 180;
    const n = OUTWARD[edge];
    const nx = n.x * Math.cos(rad) - n.y * Math.sin(rad);
    const ny = n.x * Math.sin(rad) + n.y * Math.cos(rad);
    const deltaIn = ((p.x - g.startPointer.x) * nx + (p.y - g.startPointer.y) * ny) / pxPerIn;
    const { patch, halfIn } = resizeFromEdgeDelta(g.start, edge, deltaIn, snapIn);
    const horizontal = edge === "left" || edge === "right";
    const value = (horizontal ? patch.width : patch.depth) ?? g.last;
    const sw = horizontal ? halfIn * pxPerIn * stageScale : screenHalfW;
    const sh = horizontal ? screenHalfD : halfIn * pxPerIn * stageScale;
    let at: Vec2;
    let badgeAt: Vec2;
    if (adjust) {
      const knob = adjustRingLayout(sw * 2, sh * 2).knobs[edge];
      at = { x: knob.x * inv, y: knob.y * inv };
      badgeAt =
        edge === "back"
          ? { x: at.x + 52 * inv, y: at.y }
          : { x: at.x + n.x * 40 * inv, y: at.y + (n.y === 0 ? -30 : n.y * 40) * inv };
    } else {
      const pill = touchEdgeGrip(edge, sw, sh).pill;
      at = { x: pill.x * inv, y: pill.y * inv };
      const out = (horizontal ? sw : sh) + 40;
      badgeAt = { x: n.x * out * inv, y: n.y * out * inv };
    }
    e.target.position(at);
    if (snapIn > 0 && value !== g.last) pulse(edge);
    g.last = value;
    setBadge({ text: formatLength(value, unit), ...badgeAt });
    onChange(patch);
  }

  const mouseEdgeGrip = (edge: Edge) => {
    const horizontal = edge === "front" || edge === "back";
    const x = edge === "left" ? -halfW : edge === "right" ? halfW : 0;
    const y = edge === "back" ? -halfD : edge === "front" ? halfD : 0;
    const w = (horizontal ? size.edgeW : size.edgeH) * inv;
    const h = (horizontal ? size.edgeH : size.edgeW) * inv;
    return (
      <Rect
        key={edge}
        x={x}
        y={y}
        offsetX={w / 2}
        offsetY={h / 2}
        width={w}
        height={h}
        cornerRadius={4 * inv}
        fill="#ffffff"
        stroke={stroke}
        strokeWidth={1.5 * inv}
        shadowColor="#0b0d14"
        shadowBlur={4 * inv}
        shadowOpacity={0.25}
        shadowOffsetY={1 * inv}
        draggable
        hitStrokeWidth={("edgeHit" in size ? size.edgeHit : 0) * inv}
        onMouseEnter={(e) => setCursor(e, horizontal ? "ns-resize" : "ew-resize")}
        onMouseLeave={(e) => setCursor(e, "default")}
        onDragStart={(e) => {
          e.cancelBubble = true;
          begin(edge, e);
        }}
        onDragMove={(e) => {
          e.cancelBubble = true;
          handleEdgeMove(edge, e);
        }}
        onDragEnd={(e) => {
          e.cancelBubble = true;
          end(e);
        }}
      />
    );
  };

  /** Touch grip: a pill just outside the outline whose pad only reaches outward. */
  const touchEdgeGripNode = (edge: Edge) => {
    const { pill, hit } = touchEdgeGrip(edge, screenHalfW, screenHalfD);
    const hx = (hit.x - pill.x) * inv;
    const hy = (hit.y - pill.y) * inv;
    return (
      <Rect
        key={edge}
        x={pill.x * inv}
        y={pill.y * inv}
        width={pill.width * inv}
        height={pill.height * inv}
        cornerRadius={4 * inv}
        fill={flash === edge ? stroke : "#ffffff"}
        stroke={stroke}
        strokeWidth={1.5 * inv}
        shadowColor="#0b0d14"
        shadowBlur={4 * inv}
        shadowOpacity={0.25}
        shadowOffsetY={1 * inv}
        draggable
        dragDistance={TOUCH_DRAG_DISTANCE}
        hitFunc={(context, shape) => {
          context.beginPath();
          context.rect(hx, hy, hit.width * inv, hit.height * inv);
          context.closePath();
          context.fillStrokeShape(shape);
        }}
        onDragStart={(e) => {
          e.cancelBubble = true;
          begin(edge, e);
        }}
        onDragMove={(e) => {
          e.cancelBubble = true;
          handleTouchEdgeMove(edge, e);
        }}
        onDragEnd={(e) => {
          e.cancelBubble = true;
          end(e);
        }}
      />
    );
  };

  /** Stem from the back edge up to the rotation grip. */
  const rotateStem = (
    <Line
      points={[0, -halfD, 0, rotateY + gripR]}
      stroke={stroke}
      strokeWidth={1.25 * inv}
      opacity={0.8}
      listening={false}
    />
  );

  const rotateGrip = (slotY: number) => (
    <>
      <Circle
        x={0}
        y={slotY}
        radius={gripR}
        fill={flash === "rotate" ? stroke : "#ffffff"}
        stroke={stroke}
        strokeWidth={1.5 * inv}
        shadowColor="#0b0d14"
        shadowBlur={4 * inv}
        shadowOpacity={0.25}
        shadowOffsetY={1 * inv}
        draggable
        dragDistance={touch ? TOUCH_DRAG_DISTANCE : undefined}
        hitStrokeWidth={size.rotateHit * inv}
        onMouseEnter={(e) => setCursor(e, "grab")}
        onMouseLeave={(e) => setCursor(e, "default")}
        onDragStart={(e) => {
          e.cancelBubble = true;
          begin("rotate", e);
        }}
        onDragMove={(e) => {
          e.cancelBubble = true;
          handleRotateMove(e, { x: 0, y: slotY });
        }}
        onDragEnd={(e) => {
          e.cancelBubble = true;
          end(e);
        }}
      />
      {/* Small rotate glyph inside the grip. */}
      <Circle x={0} y={slotY} radius={gripR * 0.38} fill={stroke} listening={false} />
    </>
  );

  function renderAdjust(r: NonNullable<typeof ring>) {
    const knobR = ADJUST_RING.knobR * inv;
    const knobHitStroke = (ADJUST_RING.knobHit - ADJUST_RING.knobR * 2) * inv;
    const pad = 4 * inv;
    const handles: TouchHandle[] = [...EDGES, "rotate"];
    const field = (h: TouchHandle): KnobField =>
      h === "rotate" ? "rotation" : h === "left" || h === "right" ? "width" : "depth";
    return (
      <>
        {/* Accent ring: this piece is the one being adjusted. */}
        <Rect
          x={-halfW - pad}
          y={-halfD - pad}
          width={halfW * 2 + pad * 2}
          height={halfD * 2 + pad * 2}
          cornerRadius={4 * inv}
          stroke={stroke}
          strokeWidth={2.5 * inv}
          shadowColor={stroke}
          shadowBlur={10 * inv}
          shadowOpacity={0.45}
          listening={false}
          name="adjust-ring"
        />
        {handles.map((h) => {
          const [a, b] = r.guides[h];
          return (
            <Line
              key={`guide-${h}`}
              points={[a.x * inv, a.y * inv, b.x * inv, b.y * inv]}
              stroke={stroke}
              strokeWidth={(flash === h ? 3 : 1.5) * inv}
              dash={[4 * inv, 3 * inv]}
              opacity={flash === h ? 1 : 0.75}
              listening={false}
            />
          );
        })}
        {handles.map(adjustKnobNode)}
      </>
    );

    function adjustKnobNode(h: TouchHandle) {
      const k = r.knobs[h];
      const lit = flash === h;
      const horizontal = h === "left" || h === "right";
      const glyph = 5 * inv;
      return (
        <Group
          key={`knob-${h}`}
          name={`adjust-knob adjust-knob-${h}`}
          x={k.x * inv}
          y={k.y * inv}
          draggable
          dragDistance={TOUCH_DRAG_DISTANCE}
          onMouseEnter={(e) => setCursor(e, "grab")}
          onMouseLeave={(e) => setCursor(e, "default")}
          onTap={(e) => {
            e.cancelBubble = true;
            onKnobTap?.(field(h));
          }}
          onClick={(e) => {
            e.cancelBubble = true;
            onKnobTap?.(field(h));
          }}
          onDragStart={(e) => {
            e.cancelBubble = true;
            begin(h, e);
          }}
          onDragMove={(e) => {
            e.cancelBubble = true;
            if (h === "rotate") handleRotateMove(e, { x: k.x * inv, y: k.y * inv });
            else handleTouchEdgeMove(h, e);
          }}
          onDragEnd={(e) => {
            e.cancelBubble = true;
            end(e);
          }}
        >
          <Circle
            radius={knobR}
            fill={lit ? stroke : "#ffffff"}
            stroke={stroke}
            strokeWidth={2 * inv}
            shadowColor="#0b0d14"
            shadowBlur={6 * inv}
            shadowOpacity={0.3}
            shadowOffsetY={1 * inv}
            hitStrokeWidth={knobHitStroke}
          />
          {h === "rotate" ? (
            <Circle radius={knobR * 0.36} fill={lit ? "#ffffff" : stroke} listening={false} />
          ) : (
            <Arrow
              points={horizontal ? [-glyph, 0, glyph, 0] : [0, -glyph, 0, glyph]}
              pointerAtBeginning
              pointerLength={3 * inv}
              pointerWidth={4.5 * inv}
              stroke={lit ? "#ffffff" : stroke}
              fill={lit ? "#ffffff" : stroke}
              strokeWidth={1.5 * inv}
              listening={false}
            />
          )}
        </Group>
      );
    }
  }

  let body: ReactNode;
  if (ring) {
    body = renderAdjust(ring);
  } else if (touch) {
    body = layout?.show ? (
      <>
        {layout.rotate && rotateStem}
        {layout.edges.map(touchEdgeGripNode)}
        {layout.rotate && rotateGrip(rotateY)}
      </>
    ) : null;
  } else {
    body = (
      <>
        {rotateStem}
        {EDGES.map(mouseEdgeGrip)}
        {/* After the edge pads so a grab on the rotate grip wins where the
            back edge's outward hit passes under it. */}
        {rotateGrip(rotateY)}
      </>
    );
  }

  return (
    <Group x={center.x} y={center.y} rotation={f.rotation} name="furniture-handles">
      {body}
      {badge && (
        <Label
          x={badge.x}
          y={badge.y}
          listening={false}
          rotation={-f.rotation}
          scaleX={inv}
          scaleY={inv}
          offsetX={32}
          offsetY={11}
        >
          <Tag fill={flash ? stroke : "#15151a"} cornerRadius={6} opacity={0.92} />
          <Text
            text={badge.text}
            width={64}
            align="center"
            fontSize={11}
            fontFamily="Inter, sans-serif"
            fontStyle="600"
            fill="#ffffff"
            padding={5}
          />
        </Label>
      )}
    </Group>
  );
}

function setCursor(e: KonvaEventObject<MouseEvent>, cursor: string) {
  const stage = e.target.getStage();
  if (stage) stage.container().style.cursor = cursor;
}
